import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/Button";
import Link from "next/link";
import useCurrentAccount from "@/components/context/useCurrentAccount";

export default function ReferralTermsPage() {
  const { account } = useCurrentAccount();
  return (
    <div className="min-h-screen bg-background">
      <Head>
        {generateNextSeo({
          title: "Referral Program Terms | Uptime Monitor",
          description:
            "Simple, clear terms for the Uptime Monitor referral program. Learn about commission rates, payout rules, and program guidelines.",
          canonical: "https://uptime-monitor.io/referral-terms",
        })}
      </Head>

      <Header />

      <main className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold tracking-tight mb-4">Referral Program Terms</h1>
            <p className="text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          {/* Commission Overview */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Commission Structure</h2>
            <p className="mb-4">
              You earn <strong>20% lifetime commission</strong> on every subscription payment your
              referrals make, for as long as they stay subscribed.
            </p>

            <h3 className="text-lg font-medium mb-2">Examples</h3>
            <ul className="list-disc list-inside mb-4 space-y-1">
              <li>Customer pays $24.95/month → You earn $4.99/month</li>
              <li>Customer pays $49.95/month → You earn $9.99/month</li>
              <li>Customer gets 20% discount → Your commission is also reduced by 20%</li>
            </ul>

            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> Commission only applies to subscription payments, not one-time
              purchases like email packages.
            </p>
          </div>

          {/* Payment Terms */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Payment Terms</h2>
            <p className="mb-2">
              Request payouts anytime via PayPal once you reach <strong>$100</strong>.
            </p>
            <p className="text-sm text-muted-foreground">PayPal fees are deducted from payments.</p>
          </div>

          {/* How Commission Works */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">How Commission Works</h2>

            <h3 className="text-lg font-medium mb-2">Hold Period</h3>
            <p className="mb-4">
              All commissions are held for 30 days before becoming available for payout.
            </p>
          </div>

          {/* Attribution Rules */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">How Attribution Works</h2>
            <p className="mb-4">
              Users have a <strong>30-day window</strong> to signup after clicking your referral
              link. If they subscribe later, you still earn commission. Last-click attribution
              applies - the most recent referral link gets credit.
            </p>
          </div>

          {/* Rules & Restrictions */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Rules</h2>
            <p>
              These activities are prohibited: self-referrals, spam, coupon sites, brand misuse, or
              claiming official affiliation.
            </p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Important Notes</h2>

            <h3 className="text-lg font-medium mb-2">No Commission Earned</h3>
            <ul className="list-disc list-inside mb-4 space-y-1">
              <li>Free trial users (until they start paying)</li>
              <li>One-time purchases (email packages)</li>
              <li>Fraudulent or self-referred accounts</li>
            </ul>

            <h3 className="text-lg font-medium mb-2">Program Terms</h3>
            <ul className="list-disc list-inside mb-4 space-y-1">
              <li>Existing referrals keep original rates if terms change</li>
              <li>Account termination for rule violations</li>
              <li>No earnings guarantees</li>
            </ul>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Contact</h2>
            <p className="mb-2">
              Questions? Email{" "}
              <a href="mailto:hi@uptime-monitor.io" className="text-primary hover:underline">
                hi@uptime-monitor.io
              </a>
            </p>
            <p className="text-sm text-muted-foreground">
              These terms are governed by New York State law.
            </p>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-primary/5 rounded-lg p-8">
            <h3 className="font-semibold mb-2">Ready to Start Earning?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your free account and get your referral link in seconds
            </p>
            {account ? (
              <Link href="/dashboard/referral">
                <Button size="lg">Go to Referral Dashboard →</Button>
              </Link>
            ) : (
              <Link href="/accounts/login">
                <Button size="lg">Get Your Referral Link →</Button>
              </Link>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
